/**
 * Admin → Programs → Detail editor (iter 83)
 *
 * Full editing surface for a flagship program:
 *   * Metadata (name + 3 facing descriptions + compliance + states)
 *   * Tiers: edit name/summary/patient_price/recurring/inclusions/refund-cancel-recurring rules
 *   * Services: edit vendor cost, clinic retail, payment route (6 options),
 *     clinic-purchase-allowed toggle, Nodera fee type/value, compliance flag,
 *     state availability, requires-prescription/provider-order/patient-consent,
 *     required documentation
 *   * Live margin breakdown card per tier — recomputed server-side after each save
 *
 * Saves go through `PATCH /admin/flagship-programs/{id}` which validates
 * services + tiers via Pydantic on the backend.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertCircle, ArrowLeft, CheckCircle2, ChevronDown, ChevronRight, DollarSign,
  Layers, Lock, Plus, Save, ShieldCheck, Trash2, Unlock,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { devWarn } from "@/lib/devlog";

const COMPLIANCE_OPTIONS = [
  { value: "low", label: "Low review needed" },
  { value: "counsel_required", label: "Counsel review required" },
  { value: "restricted", label: "Restricted" },
  { value: "disabled", label: "Disabled" },
];

const PAYMENT_ROUTE_OPTIONS = [
  { value: "clinic_owned", label: "Clinic-owned service" },
  { value: "clinic_purchased_vendor", label: "Clinic-purchased vendor service" },
  { value: "direct_vendor_split", label: "Direct vendor split" },
  { value: "dme_supplier_direct", label: "DME supplier direct split" },
  { value: "pharmacy_direct", label: "Pharmacy direct split" },
  { value: "professional_service_direct", label: "Professional service direct split" },
];

const NODERA_FEE_TYPES = [
  { value: "flat_amount", label: "Flat $ amount" },
  { value: "percent_of_service_revenue", label: "% of service revenue" },
];

const RECURRING_INTERVALS = [
  { value: "month", label: "Monthly" },
  { value: "quarter", label: "Quarterly" },
  { value: "year", label: "Annual" },
];

function newId() {
  return Math.random().toString(36).slice(2, 10) + Math.random().toString(36).slice(2, 10);
}

function Money({ cents }) {
  return <span className="tabular">${((cents || 0) / 100).toFixed(2)}</span>;
}

/**
 * Heuristic compliance-flag suggestion. Returns the *strongest* flag
 * implied by the service's current gates + payment route. Used to nudge
 * the admin away from undergrading a regulated service.
 *
 * Order of escalation (strongest wins):
 *   restricted        — controlled substance, DEA/state license docs, pharmacy/DME route
 *   counsel_required  — Rx, provider order, clinic-purchased vendor passthrough, professional direct
 *   low               — clinic-owned with no regulated gates
 */
function suggestComplianceFlag(svc) {
  const docs = (svc.requires_documentation || []).map((s) => String(s).toLowerCase());
  const docHints = docs.join(" ");
  const route = svc.payment_route || "clinic_owned";
  if (
    route === "pharmacy_direct" ||
    route === "dme_supplier_direct" ||
    /dea|state licens|controlled subst|schedule [iv]/i.test(docHints)
  ) {
    return "restricted";
  }
  if (
    svc.requires_prescription ||
    svc.requires_provider_order ||
    route === "clinic_purchased_vendor" ||
    route === "professional_service_direct" ||
    /baa|hipaa|services agreement|professional services/i.test(docHints)
  ) {
    return "counsel_required";
  }
  return "low";
}

const FLAG_RANK = { low: 0, counsel_required: 1, restricted: 2, disabled: 3 };

/**
 * Required-documentation completeness check.
 *
 * For each *included* service in a tier, return the doc keywords we'd
 * expect to see based on the service's payment route + gates. The check
 * is a heuristic — false positives are fine, false negatives are not
 * (the goal is catching counsel-required services missing their paper trail).
 *
 * Returns: { serviceId: { name, missing: ['Lab vendor agreement', …] } }
 * Only populated when the service has at least one missing required doc.
 */
function suggestedDocsForService(svc) {
  const route = svc.payment_route || "clinic_owned";
  const docs = (svc.requires_documentation || []).map((s) => String(s).toLowerCase());
  const has = (kw) => docs.some((d) => d.includes(kw.toLowerCase()));
  const missing = [];
  if (route === "clinic_purchased_vendor") {
    if (!has("vendor agreement") && !has("vendor contract")) {
      missing.push("Vendor agreement (clinic-purchased route)");
    }
  }
  if (route === "professional_service_direct") {
    if (!has("professional services") && !has("provider services")) {
      missing.push("Professional services agreement");
    }
  }
  if (route === "pharmacy_direct") {
    if (!has("pharmacy")) missing.push("Pharmacy partner agreement");
    if (!has("dea") && !has("licens")) missing.push("DEA / state licensure");
  }
  if (route === "dme_supplier_direct") {
    if (!has("dme") && !has("supplier")) missing.push("DME supplier agreement");
  }
  if (svc.requires_patient_consent && !has("consent") && !has("baa") && !has("hipaa")) {
    missing.push("Patient consent / HIPAA BAA");
  }
  if (svc.requires_provider_order && !has("provider order") && !has("lab order") && !has("rx")) {
    missing.push("Provider / lab order template");
  }
  return missing;
}

function tierDocGaps(program, tier) {
  const services = program?.services || [];
  const includedIds = new Set(tier?.included_service_ids || []);
  const gaps = {};
  for (const svc of services) {
    if (!includedIds.has(svc.id)) continue;
    const missing = suggestedDocsForService(svc);
    if (missing.length > 0) gaps[svc.id] = { name: svc.name, missing };
  }
  return gaps;
}

/**
 * State-availability gap check for a tier.
 *
 * For each *included* service that has a non-empty `available_states` list,
 * compare against the program's `available_states` and return any states
 * the program covers but the service doesn't (i.e. states where the program
 * is sellable but the service can't be delivered).
 *
 * `program.available_states` empty means "all US states" — when that's the
 * case we can't compute concrete gaps because we'd need to enumerate every
 * possible state the service might be missing, so we skip the warning.
 * `service.available_states` empty means "all states" — that service is
 * always good, so it never produces gaps.
 *
 * Returns: { serviceId: { name, missingStates: [...] } } — empty when no gaps.
 */
function tierStateGaps(program, tier) {
  const programStates = (program?.available_states || []).map((s) => String(s).toUpperCase());
  if (programStates.length === 0) return {}; // "all states" — nothing to check.
  const services = program?.services || [];
  const includedIds = new Set(tier?.included_service_ids || []);
  const gaps = {};
  for (const svc of services) {
    if (!includedIds.has(svc.id)) continue;
    const svcStates = (svc.available_states || []).map((s) => String(s).toUpperCase());
    if (svcStates.length === 0) continue; // service available everywhere.
    const missing = programStates.filter((st) => !svcStates.includes(st));
    if (missing.length > 0) gaps[svc.id] = { name: svc.name, missingStates: missing };
  }
  return gaps;
}

// ---------------- Service row editor ----------------
function ServiceRow({ svc, onChange, onRemove }) {
  const [open, setOpen] = useState(false);
  const update = (patch) => onChange({ ...svc, ...patch });
  const updateRange = (field, key, valueDollars) => {
    update({
      [field]: {
        ...(svc[field] || {}),
        [key]: Math.round((parseFloat(valueDollars) || 0) * 100),
      },
    });
  };
  return (
    <div
      data-testid={`flagship-service-row-${svc.id}`}
      className="rounded-lg border border-slate-800 bg-slate-900/40"
    >
      <div className="px-3 py-2 flex items-center gap-2">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="text-slate-400 hover:text-slate-200"
          aria-label="Toggle service detail"
        >
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        <input
          type="text"
          value={svc.name || ""}
          onChange={(e) => update({ name: e.target.value })}
          className="flex-1 bg-transparent text-sm font-semibold text-slate-100 focus:outline-none focus:ring-1 focus:ring-blue-500/40 rounded px-1"
          placeholder="Service name"
          data-testid={`flagship-svc-name-${svc.id}`}
        />
        <select
          value={svc.payment_route}
          onChange={(e) => update({ payment_route: e.target.value })}
          data-testid={`flagship-svc-route-${svc.id}`}
          className="text-[10px] bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-200"
        >
          {PAYMENT_ROUTE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => {
            if (window.confirm(`Remove service "${svc.name || svc.id}"?`)) onRemove();
          }}
          className="text-rose-400/70 hover:text-rose-400"
          data-testid={`flagship-svc-remove-${svc.id}`}
          aria-label="Remove service"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      {open && (
        <div className="border-t border-slate-800 px-3 py-3 grid grid-cols-2 gap-3 text-xs">
          <label className="col-span-2 block">
            <span className="text-slate-400 text-[10px] uppercase font-semibold tracking-wider">Description</span>
            <input
              type="text"
              value={svc.description || ""}
              onChange={(e) => update({ description: e.target.value })}
              className="mt-1 w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100"
              data-testid={`flagship-svc-desc-${svc.id}`}
            />
          </label>
          <label className="block">
            <span className="text-slate-400 text-[10px] uppercase font-semibold tracking-wider">Vendor cost low ($)</span>
            <input
              type="number" min="0" step="0.01"
              value={((svc.vendor_cost?.low_cents || 0) / 100).toFixed(2)}
              onChange={(e) => updateRange("vendor_cost", "low_cents", e.target.value)}
              className="mt-1 w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100"
            />
          </label>
          <label className="block">
            <span className="text-slate-400 text-[10px] uppercase font-semibold tracking-wider">Vendor cost high ($)</span>
            <input
              type="number" min="0" step="0.01"
              value={((svc.vendor_cost?.high_cents || 0) / 100).toFixed(2)}
              onChange={(e) => updateRange("vendor_cost", "high_cents", e.target.value)}
              className="mt-1 w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100"
            />
          </label>
          <label className="block">
            <span className="text-slate-400 text-[10px] uppercase font-semibold tracking-wider">Clinic retail low ($)</span>
            <input
              type="number" min="0" step="0.01"
              value={((svc.clinic_retail_value?.low_cents || 0) / 100).toFixed(2)}
              onChange={(e) => updateRange("clinic_retail_value", "low_cents", e.target.value)}
              className="mt-1 w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100"
            />
          </label>
          <label className="block">
            <span className="text-slate-400 text-[10px] uppercase font-semibold tracking-wider">Clinic retail high ($)</span>
            <input
              type="number" min="0" step="0.01"
              value={((svc.clinic_retail_value?.high_cents || 0) / 100).toFixed(2)}
              onChange={(e) => updateRange("clinic_retail_value", "high_cents", e.target.value)}
              className="mt-1 w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100"
            />
          </label>
          <label className="block">
            <span className="text-slate-400 text-[10px] uppercase font-semibold tracking-wider">Nodera fee type</span>
            <select
              value={svc.nodera_fee_type || "flat_amount"}
              onChange={(e) => update({ nodera_fee_type: e.target.value })}
              className="mt-1 w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100"
            >
              {NODERA_FEE_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-slate-400 text-[10px] uppercase font-semibold tracking-wider">
              Nodera fee value ({svc.nodera_fee_type === "flat_amount" ? "$" : "%"})
            </span>
            <input
              type="number" min="0" step="0.01"
              value={
                svc.nodera_fee_type === "flat_amount"
                  ? ((svc.nodera_fee_value_cents || 0) / 100).toFixed(2)
                  : (svc.nodera_fee_value_percent ?? 0).toString()
              }
              onChange={(e) => {
                const v = parseFloat(e.target.value) || 0;
                if (svc.nodera_fee_type === "flat_amount") update({ nodera_fee_value_cents: Math.round(v * 100) });
                else update({ nodera_fee_value_percent: v });
              }}
              className="mt-1 w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100"
            />
          </label>
          <label className="block">
            <span className="text-slate-400 text-[10px] uppercase font-semibold tracking-wider">Compliance flag</span>
            <select
              value={svc.compliance_flag || ""}
              onChange={(e) => update({ compliance_flag: e.target.value || null })}
              className="mt-1 w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100"
              data-testid={`flagship-svc-compliance-${svc.id}`}
            >
              <option value="">(inherit from program)</option>
              {COMPLIANCE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {(() => {
              const suggested = suggestComplianceFlag(svc);
              const current = svc.compliance_flag;
              // Only nudge if (a) explicitly set lower than suggested, OR
              // (b) inherited (null) but service has regulated signals.
              const showNudge = suggested !== "low" && (
                !current
                  ? true
                  : FLAG_RANK[current] < FLAG_RANK[suggested]
              );
              if (!showNudge) return null;
              return (
                <button
                  type="button"
                  onClick={() => update({ compliance_flag: suggested })}
                  data-testid={`flagship-svc-compliance-nudge-${svc.id}`}
                  className="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-200 text-[10px] font-semibold px-2 py-0.5 hover:bg-amber-500/25"
                  title={
                    `Rx=${!!svc.requires_prescription} · provider_order=${!!svc.requires_provider_order} · ` +
                    `route=${svc.payment_route} · docs=[${(svc.requires_documentation || []).join(", ") || "—"}]`
                  }
                >
                  Suggest: {COMPLIANCE_OPTIONS.find((o) => o.value === suggested)?.label}
                </button>
              );
            })()}
          </label>
          <label className="block">
            <span className="text-slate-400 text-[10px] uppercase font-semibold tracking-wider">Available states (CSV; blank = all)</span>
            <input
              type="text"
              value={(svc.available_states || []).join(", ")}
              onChange={(e) => update({
                available_states: e.target.value.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean),
              })}
              className="mt-1 w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100"
              placeholder="TX, CA, FL"
            />
          </label>
          <label className="col-span-2 flex items-center gap-2 mt-1">
            <input
              type="checkbox"
              checked={!!svc.clinic_purchase_allowed}
              onChange={(e) => update({ clinic_purchase_allowed: e.target.checked })}
              data-testid={`flagship-svc-clinic-purchase-${svc.id}`}
            />
            <span className="text-slate-300">
              Clinic Purchase Allowed (counsel-approved gate; only meaningful when route =
              "Clinic-purchased vendor")
            </span>
          </label>
          <label className="flex items-center gap-2 text-slate-300">
            <input type="checkbox" checked={!!svc.requires_prescription} onChange={(e) => update({ requires_prescription: e.target.checked })} />
            Requires prescription
          </label>
          <label className="flex items-center gap-2 text-slate-300">
            <input type="checkbox" checked={!!svc.requires_provider_order} onChange={(e) => update({ requires_provider_order: e.target.checked })} />
            Requires provider order
          </label>
          <label className="flex items-center gap-2 text-slate-300">
            <input type="checkbox" checked={!!svc.requires_patient_consent} onChange={(e) => update({ requires_patient_consent: e.target.checked })} />
            Requires patient consent
          </label>
          <label className="block col-span-2">
            <span className="text-slate-400 text-[10px] uppercase font-semibold tracking-wider">Required documentation (CSV)</span>
            <input
              type="text"
              value={(svc.requires_documentation || []).join(", ")}
              onChange={(e) => update({
                requires_documentation: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
              })}
              className="mt-1 w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100"
              placeholder="HIPAA BAA, DME supplier agreement"
            />
          </label>
          {/* Per-program service_config dump — keeps Metabolic lab panel /
              collection methods / medication options visible + editable as
              raw JSON. Switching to typed widgets per service kind is iter
              84+ work; for now an editable JSON view is enough. */}
          {Object.keys(svc.service_config || {}).length > 0 && (
            <label className="block col-span-2">
              <span className="text-slate-400 text-[10px] uppercase font-semibold tracking-wider">Service config (program-specific)</span>
              <textarea
                rows="5"
                value={JSON.stringify(svc.service_config || {}, null, 2)}
                onChange={(e) => {
                  try {
                    const next = JSON.parse(e.target.value);
                    update({ service_config: next });
                  } catch {
                    // Drop invalid JSON silently — the textarea keeps showing
                    // user input but the model isn't updated until valid JSON.
                  }
                }}
                className="mt-1 w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100 font-mono text-[11px]"
                data-testid={`flagship-svc-config-${svc.id}`}
              />
            </label>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------- Tier card ----------------
function TierCard({ tier, services, onChange, onRemove, breakdown, breakdownLoading, stateGaps, docGaps, profitFloorPct }) {
  const update = (patch) => onChange({ ...tier, ...patch });
  const toggleService = (sid) => {
    const cur = new Set(tier.included_service_ids || []);
    if (cur.has(sid)) cur.delete(sid); else cur.add(sid);
    update({ included_service_ids: Array.from(cur) });
  };
  return (
    <div
      data-testid={`flagship-tier-card-${tier.id}`}
      className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4"
    >
      <div className="flex items-center justify-between mb-3">
        <input
          type="text"
          value={tier.name || ""}
          onChange={(e) => update({ name: e.target.value })}
          className="flex-1 bg-transparent text-base font-bold text-slate-100 focus:outline-none focus:ring-1 focus:ring-blue-500/40 rounded px-1"
          placeholder="Tier name"
        />
        <button
          type="button"
          onClick={() => {
            if (window.confirm(`Remove tier "${tier.name || tier.id}"?`)) onRemove();
          }}
          className="text-rose-400/70 hover:text-rose-400"
          data-testid={`flagship-tier-remove-${tier.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      <label className="block mb-3">
        <span className="text-slate-400 text-[10px] uppercase font-semibold tracking-wider">Summary</span>
        <input
          type="text"
          value={tier.summary || ""}
          onChange={(e) => update({ summary: e.target.value })}
          className="mt-1 w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100 text-sm"
        />
      </label>
      <div className="grid grid-cols-2 gap-3 mb-3 text-xs">
        <label className="block">
          <span className="text-slate-400 text-[10px] uppercase font-semibold tracking-wider">Patient price ($)</span>
          <input
            type="number" min="0" step="0.01"
            value={((tier.patient_price_cents || 0) / 100).toFixed(2)}
            onChange={(e) => update({ patient_price_cents: Math.round((parseFloat(e.target.value) || 0) * 100) })}
            className="mt-1 w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100"
            data-testid={`flagship-tier-price-${tier.id}`}
          />
        </label>
        <label className="block">
          <span className="text-slate-400 text-[10px] uppercase font-semibold tracking-wider">Recurring</span>
          <select
            value={tier.is_recurring ? (tier.recurring_interval || "month") : ""}
            onChange={(e) => {
              const v = e.target.value;
              if (!v) update({ is_recurring: false, recurring_interval: null });
              else update({ is_recurring: true, recurring_interval: v });
            }}
            className="mt-1 w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100"
          >
            <option value="">One-time</option>
            {RECURRING_INTERVALS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
      </div>
      <div className="mb-3">
        <div className="text-slate-400 text-[10px] uppercase font-semibold tracking-wider mb-1">Included services</div>
        <div className="grid grid-cols-1 gap-1">
          {services.length === 0 ? (
            <div className="text-xs text-slate-500 italic">No services on this program yet.</div>
          ) : (
            services.map((s) => (
              <label key={s.id} className="flex items-center gap-2 text-xs text-slate-300">
                <input
                  type="checkbox"
                  checked={(tier.included_service_ids || []).includes(s.id)}
                  onChange={() => toggleService(s.id)}
                  data-testid={`flagship-tier-svc-${tier.id}-${s.id}`}
                />
                <span className="flex-1">{s.name}</span>
                <span className="text-slate-500 text-[10px]">{s.payment_route}</span>
              </label>
            ))
          )}
        </div>
      </div>
      <label className="block mb-2">
        <span className="text-slate-400 text-[10px] uppercase font-semibold tracking-wider">Inclusions (one per line, display-only)</span>
        <textarea
          rows="3"
          value={(tier.inclusions || []).join("\n")}
          onChange={(e) => update({ inclusions: e.target.value.split("\n").map((l) => l.trim()).filter(Boolean) })}
          className="mt-1 w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100 text-xs"
        />
      </label>
      <div className="grid grid-cols-1 gap-2 mb-3 text-xs">
        <label className="block">
          <span className="text-slate-400 text-[10px] uppercase font-semibold tracking-wider">Refund rules</span>
          <input
            type="text"
            value={tier.refund_rules || ""}
            onChange={(e) => update({ refund_rules: e.target.value })}
            className="mt-1 w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100"
          />
        </label>
        <label className="block">
          <span className="text-slate-400 text-[10px] uppercase font-semibold tracking-wider">Cancellation rules</span>
          <input
            type="text"
            value={tier.cancellation_rules || ""}
            onChange={(e) => update({ cancellation_rules: e.target.value })}
            className="mt-1 w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100"
          />
        </label>
        {tier.is_recurring && (
          <label className="block">
            <span className="text-slate-400 text-[10px] uppercase font-semibold tracking-wider">Recurring billing rules</span>
            <input
              type="text"
              value={tier.recurring_billing_rules || ""}
              onChange={(e) => update({ recurring_billing_rules: e.target.value })}
              className="mt-1 w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100"
            />
          </label>
        )}
        <label className="block">
          <span className="text-slate-400 text-[10px] uppercase font-semibold tracking-wider">Follow-up schedule</span>
          <input
            type="text"
            value={tier.follow_up_schedule || ""}
            onChange={(e) => update({ follow_up_schedule: e.target.value })}
            className="mt-1 w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100"
            data-testid={`flagship-tier-followup-${tier.id}`}
            placeholder="e.g. 30-day + 90-day provider check-in"
          />
        </label>
        <label className="block">
          <span className="text-slate-400 text-[10px] uppercase font-semibold tracking-wider">Refill schedule (medication tiers)</span>
          <input
            type="text"
            value={tier.refill_schedule || ""}
            onChange={(e) => update({ refill_schedule: e.target.value })}
            className="mt-1 w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100"
            data-testid={`flagship-tier-refill-${tier.id}`}
            placeholder="e.g. monthly fills after provider clearance"
          />
        </label>
      </div>

      {/* Profit-floor warning (red) — fires when clinic net margin is
          negative or below the configurable per-program floor. */}
      {(() => {
        if (!breakdown) return null;
        const total = breakdown.patient_total_cents || 0;
        const net = breakdown.clinic_net_margin_cents || 0;
        const floorPct = typeof profitFloorPct === "number" ? profitFloorPct : 15;
        const floorCents = Math.round(total * (floorPct / 100));
        if (net >= floorCents) return null;
        const isNegative = net < 0;
        const labelPct = total > 0 ? ((net / total) * 100).toFixed(1) : "—";
        return (
          <div
            data-testid={`flagship-tier-profit-warn-${tier.id}`}
            className="mt-2 rounded-lg border border-rose-500/40 bg-rose-950/40 p-3 text-xs"
          >
            <div className="text-rose-300 font-semibold flex items-center gap-1 mb-1">
              <AlertCircle className="w-3 h-3" /> {isNegative ? "Tier is unprofitable" : "Below profit floor"}
            </div>
            <div className="text-rose-100/90">
              Clinic net margin <span className="font-mono">${(net / 100).toFixed(2)}</span> ({labelPct}% of patient total)
              {" "}is {isNegative ? "negative" : `below the ${floorPct}% floor`} (needs ≥{" "}
              <span className="font-mono">${(floorCents / 100).toFixed(2)}</span>).
              {" "}Increase the patient price, lower vendor cost, reduce Nodera fee, or switch a vendor route to direct-vendor split.
            </div>
          </div>
        );
      })()}

      {/* State-availability gap warning */}
      {stateGaps && Object.keys(stateGaps).length > 0 && (
        <div
          data-testid={`flagship-tier-state-gaps-${tier.id}`}
          className="mt-2 rounded-lg border border-amber-500/30 bg-amber-950/30 p-3 text-xs"
        >
          <div className="text-amber-300 font-semibold flex items-center gap-1 mb-1.5">
            <AlertCircle className="w-3 h-3" /> State coverage gap
          </div>
          <div className="text-amber-100/90 mb-1">
            This tier includes services that can't be delivered in every state the program is available in:
          </div>
          <ul className="space-y-0.5 text-amber-100/80">
            {Object.entries(stateGaps).map(([sid, g]) => (
              <li key={sid} data-testid={`flagship-tier-state-gap-svc-${tier.id}-${sid}`}>
                <span className="font-semibold text-amber-100">{g.name}</span>
                <span className="text-amber-200/70"> — missing: </span>
                <span className="font-mono text-amber-50">{g.missingStates.join(", ")}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Required-doc completeness warning */}
      {docGaps && Object.keys(docGaps).length > 0 && (
        <div
          data-testid={`flagship-tier-doc-gaps-${tier.id}`}
          className="mt-2 rounded-lg border border-orange-500/40 bg-orange-950/30 p-3 text-xs"
        >
          <div className="text-orange-300 font-semibold flex items-center gap-1 mb-1.5">
            <AlertCircle className="w-3 h-3" /> Documentation gap
          </div>
          <div className="text-orange-100/90 mb-1">
            This tier includes services with required-documentation lists that look incomplete for their payment route:
          </div>
          <ul className="space-y-0.5 text-orange-100/80">
            {Object.entries(docGaps).map(([sid, g]) => (
              <li key={sid} data-testid={`flagship-tier-doc-gap-svc-${tier.id}-${sid}`}>
                <span className="font-semibold text-orange-100">{g.name}</span>
                <span className="text-orange-200/70"> — likely missing: </span>
                <span className="text-orange-50">{g.missing.join(" · ")}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Live margin breakdown */}
      <div
        data-testid={`flagship-tier-breakdown-${tier.id}`}
        className="mt-2 rounded-lg border border-emerald-500/20 bg-emerald-950/30 p-3 text-xs"
      >
        <div className="text-emerald-300 font-semibold flex items-center gap-1 mb-2">
          <DollarSign className="w-3 h-3" /> Margin breakdown (saved values)
        </div>
        {breakdownLoading ? (
          <div className="text-emerald-200/70">Calculating…</div>
        ) : !breakdown ? (
          <div className="text-emerald-200/70">Save the program to recompute.</div>
        ) : (
          <div className="grid grid-cols-2 gap-1 text-emerald-100/90">
            <div>Patient total: <Money cents={breakdown.patient_total_cents} /></div>
            <div>Clinic net margin: <Money cents={breakdown.clinic_net_margin_cents} /></div>
            <div>Clinic gross revenue: <Money cents={breakdown.clinic_gross_revenue_cents} /></div>
            <div>Vendor passthrough: <Money cents={breakdown.vendor_costs_within_clinic_revenue_cents} /></div>
            <div>Vendor direct splits: <Money cents={breakdown.vendor_direct_payments_cents} /></div>
            <div>Nodera fees: <Money cents={breakdown.nodera_fee_total_cents} /></div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------- Publish-gate panel ----------------
function PublishReadinessPanel({ program, readiness, readinessLoading, onToggleActive, onRefresh }) {
  const ready = readiness?.ready === true;
  const blocked = readiness && readiness.ready === false;
  const issuesByTier = (readiness?.tiers || []).filter((t) => (t.issues || []).length > 0);

  return (
    <section
      data-testid="flagship-publish-gate"
      className={`rounded-2xl border p-5 mb-6 ${
        blocked
          ? "border-rose-500/40 bg-rose-950/20"
          : ready
            ? "border-emerald-500/40 bg-emerald-950/20"
            : "border-slate-800 bg-slate-900/40"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            {blocked ? (
              <Lock className="w-4 h-4 text-rose-300" />
            ) : ready ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-300" />
            ) : (
              <ShieldCheck className="w-4 h-4 text-slate-400" />
            )}
            <div className="text-sm font-semibold text-slate-100">
              Publish gate
            </div>
            {readinessLoading && (
              <span className="text-[10px] uppercase tracking-wider text-slate-500">checking…</span>
            )}
          </div>
          <div className="text-xs text-slate-400">
            {blocked
              ? "Resolve the issues below before this program can be activated."
              : ready
                ? "All active tiers pass documentation, state-coverage, and profit-floor checks."
                : "Run a check to see whether this program is ready to publish."}
          </div>
        </div>

        <label
          className="inline-flex items-center gap-2 text-xs text-slate-200 select-none"
          title={
            blocked && !program.is_active
              ? "Publish gate is blocking activation. Resolve the issues below first — the save will fail with 409 until then."
              : ""
          }
        >
          <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">Active</span>
          <button
            type="button"
            data-testid="flagship-active-toggle"
            aria-pressed={!!program.is_active}
            onClick={() => onToggleActive(!program.is_active)}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
              program.is_active ? "bg-emerald-500" : "bg-slate-700"
            } ${blocked && !program.is_active ? "ring-1 ring-rose-500/40" : ""}`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                program.is_active ? "translate-x-4" : "translate-x-1"
              }`}
            />
          </button>
          <button
            type="button"
            onClick={onRefresh}
            data-testid="flagship-recheck-readiness"
            className="ml-1 text-[10px] uppercase tracking-wider text-slate-400 hover:text-slate-200 underline"
          >
            recheck
          </button>
        </label>
      </div>

      {blocked && (
        <div
          data-testid="flagship-publish-issues"
          className="mt-3 space-y-2"
        >
          {issuesByTier.length === 0 ? (
            <div className="text-xs text-rose-200/80">
              {readiness?.blocked_reason || "Program is not publish-ready."}
            </div>
          ) : (
            issuesByTier.map((t) => (
              <div
                key={t.tier_id}
                data-testid={`flagship-publish-tier-${t.tier_id}`}
                className="rounded-lg border border-rose-500/30 bg-rose-950/30 p-3"
              >
                <div className="text-xs font-semibold text-rose-200 mb-1">
                  {t.tier_name || "Tier"} — {t.issues.length} issue{t.issues.length === 1 ? "" : "s"}
                </div>
                <ul className="text-[11px] text-rose-100/85 space-y-1 list-disc list-inside">
                  {t.issues.map((iss, idx) => (
                    <li key={idx}>
                      {iss.kind === "doc_gap" && (
                        <>
                          <span className="font-semibold">{iss.service_name}</span> — likely missing:{" "}
                          {(iss.missing || []).join("; ")}
                        </>
                      )}
                      {iss.kind === "state_gap" && (
                        <>
                          <span className="font-semibold">{iss.service_name}</span> — not credentialed in:{" "}
                          {(iss.missing_states || []).join(", ")}
                        </>
                      )}
                      {iss.kind === "below_profit_floor" && (
                        <>
                          Below profit floor: net{" "}
                          ${((iss.clinic_net_margin_cents || 0) / 100).toFixed(2)} &lt; floor{" "}
                          ${((iss.floor_cents || 0) / 100).toFixed(2)}{" "}
                          ({(iss.floor_percent || 0).toFixed(1)}% of patient total)
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))
          )}
        </div>
      )}
    </section>
  );
}

// ---------------- Page ----------------
export default function AdminProgramDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [breakdowns, setBreakdowns] = useState({});
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [readiness, setReadiness] = useState(null);
  const [readinessLoading, setReadinessLoading] = useState(false);

  const loadReadiness = async (pid) => {
    if (!pid) return;
    setReadinessLoading(true);
    try {
      const r = await api.get(`/admin/flagship-programs/${pid}/publish-readiness`);
      setReadiness(r.data);
    } catch (e) {
      devWarn("publish-readiness fetch failed", e);
      setReadiness(null);
    } finally {
      setReadinessLoading(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/flagship-programs/${id}`);
      setProgram(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const loadBreakdowns = async (p) => {
    if (!p) return;
    setBreakdownLoading(true);
    try {
      const results = await Promise.all(
        (p.tiers || []).map((t) =>
          api.get(`/admin/flagship-programs/${p.id}/tiers/${t.id}/breakdown`).then((r) => [t.id, r.data])
        )
      );
      setBreakdowns(Object.fromEntries(results));
    } catch (e) {
      // Breakdowns are best-effort; don't block the page.
      devWarn("breakdown fetch failed", e);
    } finally {
      setBreakdownLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (program) loadBreakdowns(program);
    if (program) loadReadiness(program.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [program?.id]);

  const save = async () => {
    if (!program) return;
    setSaving(true);
    try {
      const patch = {
        name: program.name,
        description: program.description,
        clinic_facing_description: program.clinic_facing_description,
        patient_facing_description: program.patient_facing_description,
        compliance_flag: program.compliance_flag,
        available_states: program.available_states || [],
        services: program.services || [],
        tiers: program.tiers || [],
        is_active: !!program.is_active,
        sort_order: program.sort_order || 0,
        profit_margin_floor_percent: program.profit_margin_floor_percent ?? 15,
      };
      const r = await api.patch(`/admin/flagship-programs/${id}`, patch);
      setProgram(r.data);
      toast.success("Program saved.");
      await loadBreakdowns(r.data);
      await loadReadiness(r.data.id);
    } catch (e) {
      // Publish-gate 409 → keep is_active=false in UI and surface a clear toast.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409 && detail && typeof detail === "object" && detail.code === "publish_blocked") {
        setProgram((p) => ({ ...p, is_active: false }));
        if (detail.readiness) setReadiness(detail.readiness);
        toast.error("Publish blocked — resolve the issues listed below before activating.");
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setSaving(false);
    }
  };

  const deleteProgram = async () => {
    if (!window.confirm(`Delete "${program?.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/flagship-programs/${id}`);
      toast.success("Program deleted.");
      navigate("/admin/programs");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const addService = () => {
    setProgram((p) => ({
      ...p,
      services: [
        ...(p.services || []),
        {
          id: newId(),
          name: "New service",
          description: "",
          vendor_cost: { low_cents: 0, high_cents: 0 },
          clinic_retail_value: { low_cents: 0, high_cents: 0 },
          patient_price_cents: null,
          payment_route: "clinic_owned",
          clinic_purchase_allowed: false,
          nodera_fee_type: "flat_amount",
          nodera_fee_value_cents: 0,
          nodera_fee_value_percent: 0,
          compliance_flag: null,
          available_states: [],
          vendor_id: null,
          vendor_geography: [],
          requires_documentation: [],
          requires_prescription: false,
          requires_provider_order: false,
          requires_patient_consent: false,
        },
      ],
    }));
  };

  const addTier = () => {
    setProgram((p) => ({
      ...p,
      tiers: [
        ...(p.tiers || []),
        {
          id: newId(),
          name: "New tier",
          summary: "",
          patient_price_cents: 0,
          is_recurring: false,
          recurring_interval: null,
          included_service_ids: [],
          inclusions: [],
          refund_rules: "",
          cancellation_rules: "",
          recurring_billing_rules: null,
          is_active: true,
        },
      ],
    }));
  };

  const updateService = (sid, next) => {
    setProgram((p) => ({
      ...p,
      services: (p.services || []).map((s) => (s.id === sid ? next : s)),
    }));
  };
  const removeService = (sid) => {
    setProgram((p) => ({
      ...p,
      services: (p.services || []).filter((s) => s.id !== sid),
      tiers: (p.tiers || []).map((t) => ({
        ...t,
        included_service_ids: (t.included_service_ids || []).filter((x) => x !== sid),
      })),
    }));
  };
  const updateTier = (tid, next) => {
    setProgram((p) => ({
      ...p,
      tiers: (p.tiers || []).map((t) => (t.id === tid ? next : t)),
    }));
  };
  const removeTier = (tid) => {
    setProgram((p) => ({ ...p, tiers: (p.tiers || []).filter((t) => t.id !== tid) }));
  };

  const services = program?.services || [];
  const tiers = program?.tiers || [];

  if (loading || !program) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 px-6 py-10">
        <div className="text-sm text-slate-500">Loading program…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="max-w-6xl mx-auto px-6 py-10">
        <Link
          to="/admin/programs"
          className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-200 mb-4"
        >
          <ArrowLeft className="w-3 h-3" /> Back to programs
        </Link>
        <div className="flex items-start justify-between mb-6">
          <div className="flex-1 mr-4">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">
              {program.slug}
            </div>
            <input
              type="text"
              value={program.name || ""}
              onChange={(e) => setProgram({ ...program, name: e.target.value })}
              className="mt-1 w-full bg-transparent text-3xl font-bold text-slate-100 focus:outline-none focus:ring-1 focus:ring-blue-500/40 rounded px-1"
              data-testid="flagship-program-name"
            />
          </div>
          <div className="flex items-center gap-2">
            <span
              data-testid="flagship-active-pill"
              className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-semibold uppercase tracking-wider border ${
                program.is_active
                  ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-200"
                  : "bg-slate-800 border-slate-700 text-slate-300"
              }`}
            >
              {program.is_active ? <Unlock className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
              {program.is_active ? "Active" : "Draft"}
            </span>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              data-testid="flagship-save-program"
              className="inline-flex items-center gap-1 rounded-full bg-blue-600 text-white text-xs font-semibold px-3 py-1.5 hover:bg-blue-700 disabled:opacity-50"
            >
              <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              onClick={deleteProgram}
              data-testid="flagship-delete-program"
              className="inline-flex items-center gap-1 rounded-full bg-rose-600/20 border border-rose-500/40 text-rose-300 text-xs font-semibold px-3 py-1.5 hover:bg-rose-600/30"
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
          </div>
        </div>

        <PublishReadinessPanel
          program={program}
          readiness={readiness}
          readinessLoading={readinessLoading}
          onToggleActive={(next) => setProgram((p) => ({ ...p, is_active: next }))}
          onRefresh={() => loadReadiness(program.id)}
        />

        {/* Program metadata */}
        <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5 mb-6">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-3">
            Program meta
          </div>
          <label className="block mb-3">
            <span className="text-slate-400 text-[10px] uppercase font-semibold tracking-wider">Internal description</span>
            <textarea
              rows="2"
              value={program.description || ""}
              onChange={(e) => setProgram({ ...program, description: e.target.value })}
              className="mt-1 w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100 text-sm"
            />
          </label>
          <label className="block mb-3">
            <span className="text-slate-400 text-[10px] uppercase font-semibold tracking-wider">Clinic-facing description</span>
            <textarea
              rows="2"
              value={program.clinic_facing_description || ""}
              onChange={(e) => setProgram({ ...program, clinic_facing_description: e.target.value })}
              className="mt-1 w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100 text-sm"
              data-testid="flagship-clinic-desc"
            />
          </label>
          <label className="block mb-3">
            <span className="text-slate-400 text-[10px] uppercase font-semibold tracking-wider">Patient-facing description</span>
            <textarea
              rows="2"
              value={program.patient_facing_description || ""}
              onChange={(e) => setProgram({ ...program, patient_facing_description: e.target.value })}
              className="mt-1 w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100 text-sm"
              data-testid="flagship-patient-desc"
            />
          </label>
          <div className="grid grid-cols-3 gap-3 text-xs">
            <label className="block">
              <span className="text-slate-400 text-[10px] uppercase font-semibold tracking-wider flex items-center gap-1">
                <ShieldCheck className="w-3 h-3" /> Compliance flag
              </span>
              <select
                value={program.compliance_flag}
                onChange={(e) => setProgram({ ...program, compliance_flag: e.target.value })}
                className="mt-1 w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100"
                data-testid="flagship-program-compliance"
              >
                {COMPLIANCE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-slate-400 text-[10px] uppercase font-semibold tracking-wider">Available states (CSV; blank = all)</span>
              <input
                type="text"
                value={(program.available_states || []).join(", ")}
                onChange={(e) => setProgram({
                  ...program,
                  available_states: e.target.value.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean),
                })}
                className="mt-1 w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100"
              />
            </label>
            <label className="block">
              <span className="text-slate-400 text-[10px] uppercase font-semibold tracking-wider">
                Profit floor (% of patient total)
              </span>
              <input
                type="number" min="0" max="100" step="0.5"
                value={program.profit_margin_floor_percent ?? 15}
                onChange={(e) => setProgram({
                  ...program,
                  profit_margin_floor_percent: Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)),
                })}
                data-testid="flagship-program-profit-floor"
                className="mt-1 w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100"
              />
            </label>
          </div>
        </section>

        {/* Services */}
        <section className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold text-slate-200 flex items-center gap-2">
              Services ({services.length})
            </div>
            <button
              type="button"
              onClick={addService}
              data-testid="flagship-add-service"
              className="inline-flex items-center gap-1 rounded-full bg-slate-800 border border-slate-700 text-xs font-semibold px-2 py-1 text-slate-200 hover:bg-slate-700"
            >
              <Plus className="w-3 h-3" /> Add service
            </button>
          </div>
          {services.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-800 bg-slate-900/30 p-6 text-center text-xs text-slate-500">
              No services yet. Add one above to start building tiers.
            </div>
          ) : (
            <div className="space-y-2">
              {services.map((s) => (
                <ServiceRow
                  key={s.id}
                  svc={s}
                  onChange={(next) => updateService(s.id, next)}
                  onRemove={() => removeService(s.id)}
                />
              ))}
            </div>
          )}
        </section>

        {/* Tiers */}
        <section className="mb-12">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold text-slate-200 flex items-center gap-2">
              <Layers className="w-4 h-4" /> Tiers ({tiers.length})
            </div>
            <button
              type="button"
              onClick={addTier}
              data-testid="flagship-add-tier"
              className="inline-flex items-center gap-1 rounded-full bg-slate-800 border border-slate-700 text-xs font-semibold px-2 py-1 text-slate-200 hover:bg-slate-700"
            >
              <Plus className="w-3 h-3" /> Add tier
            </button>
          </div>
          {tiers.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-800 bg-slate-900/30 p-6 text-center text-xs text-slate-500">
              No tiers yet. Add a tier and toggle which services are included for that price.
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {tiers.map((t) => (
                <TierCard
                  key={t.id}
                  tier={t}
                  services={services}
                  breakdown={breakdowns[t.id]}
                  breakdownLoading={breakdownLoading}
                  stateGaps={tierStateGaps(program, t)}
                  docGaps={tierDocGaps(program, t)}
                  profitFloorPct={program.profit_margin_floor_percent ?? 15}
                  onChange={(next) => updateTier(t.id, next)}
                  onRemove={() => removeTier(t.id)}
                />
              ))}
            </div>
          )}
        </section>

        <div className="mt-8 rounded-2xl border border-amber-500/20 bg-amber-950/20 p-4 text-xs text-amber-200/80 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 flex-none" />
          <div>
            <div className="font-semibold text-amber-200 mb-1">Iter 84 preview</div>
            The margin breakdown numbers are based on the saved patient prices + vendor costs. The actual
            Stripe Connect destination-charge split that routes money to the clinic / DME / interpretation vendor
            / Nodera lands in iter 84 alongside the patient-facing one-checkout flow.
          </div>
        </div>
      </div>
    </div>
  );
}
